import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  setDoc, // <--- ДОБАВЛЕНО для создания записи о скрытии
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { bookingAction, newRequestId } from './bookingApi';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import moment, { romeFormat as format, parseRomeDateTime } from './config/romeTime';
import ru from 'date-fns/locale/ru';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  OPEN_HOURS_COLLECTION,
  isSlotUnavailable,
  isDayUnavailable,
  isExtraOpen,
  normalizeOpenHours,
  openHoursInterval,
  selectionToOpenHours,
} from './config/availability';
import StudentPicker, { isOfflineEmail, makeOfflineEmail } from './admin/StudentPicker';
import LessonForm from './admin/LessonForm';
import './admin/admin.css';

moment.locale('ru', {
  week: { dow: 1 },
});
const localizer = momentLocalizer(moment);

const ADMIN_EMAIL = 'ksiusha.maestra@gmail.com';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function AdminDashboard() {
  const navigate = useNavigate();

  const [recentLessons, setRecentLessons] = useState([]);
  const [unpaidLessons, setUnpaidLessons] = useState([]);

  const [credits, setCredits] = useState([]);
  // Форма «Оплата»: ученик выбирается из списка (или вписывается новый).
  const [payStudent, setPayStudent] = useState(null);
  const [newCredit, setNewCredit] = useState({
    purchased: '',
    paymentDate: moment().format('YYYY-MM-DD'),
  });

  // Короткое уведомление внизу экрана вместо alert() — не требует лишнего клика.
  const [toast, setToast] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const paymentSavingRef = useRef(false);
  const requestIds = useRef(new Map());
  const mutateBooking = async (action, data) => {
    const key = JSON.stringify({ action, data });
    if (!requestIds.current.has(key)) requestIds.current.set(key, newRequestId());
    const result = await bookingAction(action, data, requestIds.current.get(key));
    requestIds.current.delete(key);
    return result;
  };
  const showToast = (text, kind = 'ok') => setToast({ text, kind, id: Date.now() });

  const [blockedSlots, setBlockedSlots] = useState([]);
  // Часы, открытые сверх обычного расписания (например вечер после 21:00).
  const [openHours, setOpenHours] = useState([]);
  const [newOpenHours, setNewOpenHours] = useState({ date: '', startTime: '21:00', endTime: '22:00' });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [pendingBlock, setPendingBlock] = useState(null);
  const [newBlock, setNewBlock] = useState({ date: '', startTime: '07:00', endTime: '23:00', label: '' });
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [expandedPastLessons, setExpandedPastLessons] = useState(new Set());

  // Список скрытых учеников (полные данные, чтобы можно было вернуть обратно)
  const [hiddenStudentsList, setHiddenStudentsList] = useState([]);
  const [studentAccessList, setStudentAccessList] = useState([]);
  // Зарегистрированные аккаунты (пишутся при входе ученика). Могут ещё не иметь уроков.
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [adminAuthorized, setAdminAuthorized] = useState(false);

  // Ручное добавление ученика по email (для тех, кто только зарегистрировался)
  const [newStudent, setNewStudent] = useState({ email: '', name: '' });
  // Черновики дат для временной блокировки конкретного ученика (по email)
  const [blockUntilDrafts, setBlockUntilDrafts] = useState({});

  const [editingCreditId, setEditingCreditId] = useState(null);
  const [editCreditData, setEditCreditData] = useState({});
  const [expandedCreditHistory, setExpandedCreditHistory] = useState(new Set());

  const [editingBalanceEmail, setEditingBalanceEmail] = useState(null);
  const [editBalanceValue, setEditBalanceValue] = useState('');
  const [editBalanceReason, setEditBalanceReason] = useState('');
  const balanceBeforeEdit = useRef(null);
  const creditSavingRef = useRef(false);
  const [creditSaving, setCreditSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAdminAuthorized(user?.email === ADMIN_EMAIL && user?.emailVerified === true);
      if (!user || user.email !== ADMIN_EMAIL) {
        alert('Доступ запрещен. Перенаправление на главную страницу.');
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Уроки за последние 6 месяцев + все неоплаченные любой давности. Раньше здесь
  // читался весь архив уроков при каждом открытии панели — главный расход квоты.
  // Счётчики по ученику («всего / проведено / оплачено») теперь считаются за эти
  // 6 месяцев; долги не теряются, их подтягивает второй запрос.
  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    const qRecent = query(collection(db, 'lessons'), where('start', '>=', cutoff.toISOString()));
    const unsubscribe = onSnapshot(qRecent, (snapshot) => {
      setRecentLessons(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [adminAuthorized]);

  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const qUnpaid = query(collection(db, 'lessons'), where('paid', '==', false));
    const unsubscribe = onSnapshot(qUnpaid, (snapshot) => {
      setUnpaidLessons(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [adminAuthorized]);

  const lessons = useMemo(() => {
    const byId = new Map();
    recentLessons.forEach(l => byId.set(l.id, l));
    unpaidLessons.forEach(l => byId.set(l.id, l));
    return Array.from(byId.values());
  }, [recentLessons, unpaidLessons]);

  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const unsubscribe = onSnapshot(collection(db, 'credits'), (snapshot) => {
      const loadedCredits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCredits(loadedCredits);
    });
    return () => unsubscribe();
  }, [adminAuthorized]);

  useEffect(() => {
    if (!adminAuthorized) return undefined;
    // Только последние 2 месяца и будущее: старые блокировки нигде не показываются,
    // а читались при каждом открытии панели. `start` хранится строкой ISO (часть с
    // Z, часть с +02:00) — сравнение строк безопасно, т.к. решает префикс
    // YYYY-MM-DDTHH:MM, а порог заведомо далеко от «сейчас».
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 2);
    const qRecent = query(collection(db, 'blockedSlots'), where('start', '>=', cutoff.toISOString().slice(0, 19)));
    const unsubscribe = onSnapshot(qRecent, (snapshot) => {
      const loadedBlocked = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBlockedSlots(loadedBlocked);
    });
    return () => unsubscribe();
  }, [adminAuthorized]);

  // Часы, открытые сверх расписания. Читаем последний месяц и будущее —
  // прошлые записи уже ни на что не влияют.
  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 1);
    const qOpen = query(collection(db, OPEN_HOURS_COLLECTION), where('date', '>=', cutoff.toISOString().slice(0, 10)));
    const unsubscribe = onSnapshot(qOpen, (snapshot) => {
      setOpenHours(normalizeOpenHours(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    }, (error) => console.error('Ошибка загрузки открытых часов:', error));
    return () => unsubscribe();
  }, [adminAuthorized]);

  // Загрузка списка скрытых учеников
  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const unsubscribe = onSnapshot(collection(db, 'hiddenStudents'), (snapshot) => {
      const loadedHidden = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setHiddenStudentsList(loadedHidden);
    });
    return () => unsubscribe();
  }, [adminAuthorized]);

  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const unsubscribe = onSnapshot(collection(db, 'studentAccess'), (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudentAccessList(loaded);
    });
    return () => unsubscribe();
  }, [adminAuthorized]);

  useEffect(() => {
    if (!adminAuthorized) return undefined;
    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snapshot) => setRegisteredUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))),
      // Коллекции может ещё не быть / не быть прав — это не должно ломать панель
      (error) => console.warn('Коллекция users недоступна:', error.message)
    );
    return () => unsubscribe();
  }, [adminAuthorized]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.kind === 'error' ? 5000 : 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Esc закрывает всплывающие окна.
  useEffect(() => {
    if (!pendingBlock && !selectedEvent) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setPendingBlock(null); setSelectedEvent(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pendingBlock, selectedEvent]);

  const studentAccessMap = {};
  studentAccessList.forEach(sa => { studentAccessMap[sa.id] = sa; });

  const hiddenStudents = new Set(hiddenStudentsList.map(h => h.id));

  const now = new Date();

  const getStudentStats = () => {
    const studentMap = {};

    // Заводим карточку ученика независимо от того, есть ли у него уроки.
    // Возвращает null, если ученика показывать не нужно (скрыт / это админ).
    const ensureStudent = (email, name) => {
      if (!email) return null;
      if (email === ADMIN_EMAIL) return null;
      // ИЗМЕНЕНО: Если ученик в скрытом списке - пропускаем его
      if (hiddenStudents.has(email)) return null;

      if (!studentMap[email]) {
        studentMap[email] = {
          email,
          name: name || email,
          lessons: [],
          totalLessons: 0,
          paidLessons: 0,
          unpaidLessons: 0,
          futureLessons: 0,
          pastLessons: 0,
        };
      } else if (name && studentMap[email].name === email) {
        // Подставляем настоящее имя, если раньше знали только email
        studentMap[email].name = name;
      }
      return studentMap[email];
    };

    // Ученик без уроков тоже должен быть виден — иначе ему невозможно открыть
    // бронирование, а без брони у него никогда не появится первый урок.
    registeredUsers.forEach(u => ensureStudent(u.email || u.id, u.name));
    credits.forEach(c => ensureStudent(c.userEmail, c.userName));
    studentAccessList.forEach(sa => ensureStudent(sa.email || sa.id, sa.name));

    lessons.forEach(lesson => {
      const student = ensureStudent(lesson.userEmail, lesson.userName);
      if (!student) return;

      student.lessons.push(lesson);
      student.totalLessons++;

      const lessonDate = new Date(lesson.start);
      if (lessonDate < now) {
        student.pastLessons++;
        if (lesson.paid) {
          student.paidLessons++;
        } else {
          student.unpaidLessons++;
        }
      } else {
        student.futureLessons++;
      }
    });

    Object.values(studentMap).forEach(student => {
      // Считаем баланс по ВСЕМ покупкам ученика (а не по первой найденной записи),
      // плюс покупки партнёра, если он указан. Так «Баланс» здесь совпадает
      // с суммарным остатком в таблице покупок.
      const studentCredits = credits.filter(c => c.userEmail === student.email);
      if (studentCredits.length > 0) {
        let purchased = studentCredits.reduce((sum, c) => sum + (c.purchased || 0), 0);
        let booked = studentCredits.reduce((sum, c) => sum + (c.booked || 0), 0);

        const partnerEmail = studentCredits.map(c => c.partnerEmail).find(Boolean);
        if (partnerEmail) {
          const partnerCredits = credits.filter(c => c.userEmail === partnerEmail);
          purchased += partnerCredits.reduce((sum, c) => sum + (c.purchased || 0), 0);
          booked += partnerCredits.reduce((sum, c) => sum + (c.booked || 0), 0);
        }

        student.creditPurchased = purchased;
        student.creditBooked = booked;
        student.creditBalance = purchased - booked;
        student.partnerEmail = partnerEmail;
      } else {
        student.creditPurchased = 0;
        student.creditBooked = 0;
        student.creditBalance = 0;
      }
      student.lessons.sort((a, b) => new Date(b.start) - new Date(a.start));
    });

    return Object.values(studentMap).sort((a, b) => a.name.localeCompare(b.name));
  };

  const studentStats = getStudentStats();

  const toggleStudentExpand = (email) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) newSet.delete(email);
      else newSet.add(email);
      return newSet;
    });
  };

  const togglePastLessons = (email) => {
    setExpandedPastLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) newSet.delete(email);
      else newSet.add(email);
      return newSet;
    });
  };

  // --- ИЗМЕНЕНО: Функция СКРЫТИЯ ученика (вместо удаления) ---
  const handleHideStudent = async (email, name) => {
    const confirmHide = window.confirm(
        `Скрыть ученика ${name} (${email}) из списка?\n\nДанные НЕ удалятся из базы, но он перестанет отображаться здесь.`
    );

    if (!confirmHide) return;

    try {
        // Мы используем email как ID документа, чтобы легко искать
        await setDoc(doc(db, 'hiddenStudents', email), {
            email: email,
            name: name,
            hiddenAt: new Date().toISOString()
        });
        alert(`Ученик ${name} скрыт из списка.`);
    } catch (error) {
        console.error("Ошибка при скрытии ученика:", error);
        alert("Ошибка: " + error.message);
    }
  };

  // --- Возврат скрытого ученика в список ---
  const handleUnhideStudent = async (email, name) => {
    try {
        await deleteDoc(doc(db, 'hiddenStudents', email));
        alert(`Ученик ${name || email} снова отображается в списке.`);
    } catch (error) {
        console.error("Ошибка при восстановлении ученика:", error);
        alert("Ошибка: " + error.message);
    }
  };

  // Добавить ученика в список вручную по email и сразу открыть ему бронирование.
  // Нужно для только что зарегистрировавшихся: уроков у них ещё нет,
  // поэтому в списке они сами по себе не появятся.
  const handleAddStudentAccess = async () => {
    const email = newStudent.email.trim().toLowerCase();
    const name = newStudent.name.trim();

    if (!email || !email.includes('@')) {
      alert('Введите email ученика — ровно тот, с которым он зарегистрировался.');
      return;
    }

    try {
      await setDoc(doc(db, 'studentAccess', email), {
        email,
        name: name || email,
        bookingEnabled: true,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      // Если этого ученика когда-то скрыли — возвращаем в список
      if (hiddenStudents.has(email)) {
        await deleteDoc(doc(db, 'hiddenStudents', email));
      }

      alert(`Ученик ${name || email} добавлен в список. Бронирование открыто.`);
      setNewStudent({ email: '', name: '' });
    } catch (error) {
      console.error('Ошибка при добавлении ученика:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  const handleToggleBookingAccess = async (email, name, currentEnabled) => {
    try {
      await setDoc(doc(db, 'studentAccess', email), {
        email,
        name,
        bookingEnabled: !currentEnabled,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error("Ошибка при изменении доступа:", error);
      alert("Ошибка: " + error.message);
    }
  };

  // Временно заблокировать бронирование конкретному ученику до указанной даты
  // (например, за нарушение правил). Доступ восстанавливается сам собой, когда
  // дата проходит — отдельно снимать блокировку не обязательно.
  const handleSetTemporaryBlock = async (email, name, dateStr) => {
    if (!dateStr) {
      alert('Выберите дату, до которой заблокировать бронирование.');
      return;
    }
    try {
      await setDoc(doc(db, 'studentAccess', email), {
        email,
        name,
        blockedUntil: moment(dateStr, 'YYYY-MM-DD', true).endOf('day').toISOString(),
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setBlockUntilDrafts(prev => ({ ...prev, [email]: '' }));
      alert(`Бронирование для ${name || email} заблокировано до ${dateStr}.`);
    } catch (error) {
      console.error('Ошибка при установке временной блокировки:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  const handleClearTemporaryBlock = async (email) => {
    try {
      await updateDoc(doc(db, 'studentAccess', email), { blockedUntil: null });
    } catch (error) {
      console.error('Ошибка при снятии временной блокировки:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  // Новый ученик (вписан в поле выбора) → запись в studentAccess, чтобы он сразу
  // появился в списке и в подсказках. Без email получает служебный адрес.
  const resolveStudent = (student) => {
    let email = (student.email || '').trim().toLowerCase();
    const offline = !email;
    if (offline) email = makeOfflineEmail();
    const name = (student.name || '').trim() || email;
    return { email, name, offline, isNew: !!student.isNew };
  };

  // Бронирование урока от имени ученика (клик по ячейке календаря или форма «Урок вручную»).

  const bookLessons = async ({ student: rawStudent, start, duration, repeatCount, remind }) => {
    const student = resolveStudent(rawStudent);
    const slots = Array.from({ length: repeatCount + 1 }, (_, i) => ({
      start: moment.tz(start, 'Europe/Rome').add(i, 'weeks').toISOString(), duration
    }));
    const userTimezone = lessons.find(l => l.userEmail === student.email && l.userTimezone)?.userTimezone || 'Europe/Rome';
    try {
      const result = await mutateBooking('book', {
        slots, userEmail: student.email, userName: student.name, userTimezone, remind
      });
      showToast('✓ Уроки добавлены: ' + slots.length + '. Оплачено с баланса: ' + result.paid);
      return true;
    } catch (error) { showToast(error.message || 'Не удалось сохранить урок.', 'error'); return false; }
  };

  const handleDeleteLesson = async id => {
    if (!window.confirm('Отменить урок и вернуть списанный кредит? Событие преподавателя будет удалено из Google Calendar.')) return;
    try { await mutateBooking('cancel', { lessonId: id }); showToast('Урок отменён.'); }
    catch (error) { showToast(error.message, 'error'); }
  };

  const handleTogglePaid = async (lessonId, currentPaid) => {
    try { await mutateBooking('setPaid', { lessonId, paid: !currentPaid }); }
    catch (error) { showToast(error.message, 'error'); }
  };

  const handleDeleteBlockedSlot = async (id) => {
    try {
      await mutateBooking('deleteBlocks', { ids: [id] });
      alert('Заблокированный слот удалён');
      setSelectedEvent(null);
    } catch (error) {
      console.error('Ошибка при удалении заблокированного слота:', error);
      showToast(error.message || 'Не удалось удалить блокировку.', 'error');
    }
  };

  const handleClearAllBlockedSlots = async () => {
    if (!window.confirm('Удалить все ручные блокировки? События Google остаются в Google Calendar.')) {
      return;
    }
    try {
      const manual = blockedSlots.filter(slot => !slot.googleEventId && (!slot.source || slot.source === 'manual'));
      for (let i = 0; i < manual.length; i += 400)
        await mutateBooking('deleteBlocks', { ids: manual.slice(i, i + 400).map(slot => slot.id) });
      alert('Ручные блокировки удалены');
    } catch (error) {
      console.error('Ошибка при удалении:', error);
      showToast(error.message || 'Не удалось удалить блокировки.', 'error');
    }
  };

  // Клик по ячейке → окно с вкладками «Урок / Блок / Открыть». Короткое выделение
  // (клик, до 1,5 ч) — почти всегда урок; длинное или целый день — блокировка.
  const handleSelectSlot = (slotInfo) => {
    const minutes = (slotInfo.end - slotInfo.start) / 60000;
    const allDay = minutes >= 24 * 60;
    const mode = !allDay && (slotInfo.action === 'click' || minutes <= 90) ? 'lesson' : 'block';
    setPendingBlock({ start: slotInfo.start, end: slotInfo.end, label: '', mode, allDay });
  };

  const handleConfirmPendingBlock = async () => {
    if (!pendingBlock) return;
    try {
      await mutateBooking('addBlock', {
        start: pendingBlock.start.toISOString(),
        end: pendingBlock.end.toISOString(),
        title: pendingBlock.label || 'Занято',
        source: 'manual',
        createdAt: new Date().toISOString(),
      });
      setPendingBlock(null);
    } catch (error) {
      console.error('Ошибка при блокировке слота:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  const handleAddBlockedSlot = async () => {
    if (!newBlock.date || !newBlock.startTime || !newBlock.endTime) {
      alert('Выберите дату и время начала/конца');
      return;
    }
    const start = parseRomeDateTime(newBlock.date, newBlock.startTime);
    const end = parseRomeDateTime(newBlock.date, newBlock.endTime);
    if (!start || !end || end <= start) {
      alert('Время окончания должно быть позже времени начала');
      return;
    }
    try {
      await mutateBooking('addBlock', {
        start: start.toISOString(),
        end: end.toISOString(),
        title: newBlock.label || 'Занято',
        source: 'manual',
        createdAt: new Date().toISOString(),
      });
      alert('Слот заблокирован');
      setNewBlock({ date: newBlock.date, startTime: '07:00', endTime: '23:00', label: '' });
    } catch (error) {
      console.error('Ошибка при блокировке слота:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  // Открыть ученикам время, которое по обычному расписанию закрыто
  // (вечер после 17:00, выходные, дни из списка отпусков).
  // Открыть время, выделенное мышью прямо в календаре.
  const handleOpenPendingSlot = async () => {
    if (!pendingBlock) return;
    const range = selectionToOpenHours(pendingBlock.start, pendingBlock.end);
    try {
      await mutateBooking('addHours', {
        ...range,
        createdAt: new Date().toISOString(),
      });
      setPendingBlock(null);
    } catch (error) {
      console.error('Ошибка при открытии часов:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  // Убрать открытые часы, попавшие в выделение (вернуть обычное расписание).
  const handleClosePendingSlot = async (overrides) => {
    if (!overrides || overrides.length === 0) return;
    const list = overrides.map((o) => o.date + ' ' + o.startTime + '–' + o.endTime).join('\n');
    if (!window.confirm('Закрыть обратно:\n' + list)) return;
    try {
      await mutateBooking('deleteHours', { ids: overrides.map(o => o.id) });
      setPendingBlock(null);
    } catch (error) {
      console.error('Ошибка при удалении открытых часов:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  const handleAddOpenHours = async () => {
    if (!newOpenHours.date || !newOpenHours.startTime || !newOpenHours.endTime) {
      alert('Выберите дату и время начала/конца');
      return;
    }
    if (newOpenHours.endTime <= newOpenHours.startTime) {
      alert('Время окончания должно быть позже времени начала');
      return;
    }
    try {
      await mutateBooking('addHours', {
        date: newOpenHours.date,
        startTime: newOpenHours.startTime,
        endTime: newOpenHours.endTime,
        createdAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Ошибка при открытии часов:', error);
      alert('Ошибка: ' + error.message);
    }
  };


  const handleAddCredit = async () => {
    if (paymentSavingRef.current) return;
    const qty = Number(newCredit.purchased);
    if (!payStudent || !Number.isInteger(qty) || qty <= 0) {
      showToast('Выберите ученика и целое количество уроков.', 'error'); return;
    }
    const student = resolveStudent(payStudent);
    paymentSavingRef.current = true; setPaymentSaving(true);
    try {
      const result = await mutateBooking('addPayment', {
        userEmail: student.email, userName: student.name, quantity: qty, paymentDate: newCredit.paymentDate
      });
      showToast('💳 Оплата записана. Закрыто неоплаченных уроков: ' + result.paid);
      setPayStudent(null); setNewCredit({ purchased: '', paymentDate: moment().format('YYYY-MM-DD') });
    } catch (error) { showToast(error.message, 'error'); }
    finally { paymentSavingRef.current = false; setPaymentSaving(false); }
  };

  const handleDeleteCredit = async (id) => {
    if (creditSavingRef.current) return;
    if (window.confirm('Удалить неиспользованную покупку?')) {
      creditSavingRef.current = true; setCreditSaving(true);
      try {
        await mutateBooking('deleteCredit', { creditId: id });
        showToast('Покупка удалена.');
      } catch (error) {
        showToast(error.message, 'error');
      }
      finally { creditSavingRef.current = false; setCreditSaving(false); }
    }
  };

  const startEditingCredit = (credit) => {
    setEditingCreditId(credit.id);
    setEditCreditData({
        purchased: credit.purchased,
        expectedPurchased: Number(credit.purchased),
        expectedPaymentDate: credit.paymentDate || '',
        paymentDate: credit.paymentDate || (credit.createdAt ? credit.createdAt.slice(0, 10) : ''),
    });
  };

  const saveCreditEdit = async () => {
      if (creditSavingRef.current) return;
      creditSavingRef.current = true; setCreditSaving(true);
      try {
          await mutateBooking('editCredit', {
              creditId: editingCreditId,
              purchased: Number(editCreditData.purchased),
              paymentDate: editCreditData.paymentDate,
              expectedPurchased: editCreditData.expectedPurchased,
              expectedPaymentDate: editCreditData.expectedPaymentDate,
          });
          setEditingCreditId(null);
      } catch (error) {
          showToast(error.message, 'error');
      }
      finally { creditSavingRef.current = false; setCreditSaving(false); }
  };

  const cancelCreditEdit = () => {
      setEditingCreditId(null);
      setEditCreditData({});
  };

  const startEditingBalance = (student) => {
    setEditingBalanceEmail(student.email);
    setEditBalanceValue(String(student.creditBalance));
    setEditBalanceReason('');
    balanceBeforeEdit.current = student.creditBalance;
  };

  const cancelBalanceEdit = () => {
    setEditingBalanceEmail(null);
    setEditBalanceValue('');
    setEditBalanceReason('');
    balanceBeforeEdit.current = null;
  };

  // Отдельная запись сохраняет покупки и связи, необходимые для отмены уроков.
  const saveBalanceEdit = async (student) => {
    if (creditSavingRef.current) return;
    const target = Number(editBalanceValue);
    if (!Number.isInteger(target) || !editBalanceReason.trim()) {
      showToast('Введите целый остаток и причину изменения.', 'error');
      return;
    }
    creditSavingRef.current = true; setCreditSaving(true);
    try {
      await mutateBooking('adjustBalance', { userEmail: student.email, userName: student.name,
        targetBalance: target, expectedBalance: balanceBeforeEdit.current, reason: editBalanceReason.trim() });
      cancelBalanceEdit();
    } catch (error) {
      showToast(error.message, 'error');
    }
    finally { creditSavingRef.current = false; setCreditSaving(false); }
  };

  const toggleCreditHistory = (email) => {
    setExpandedCreditHistory(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) newSet.delete(email);
      else newSet.add(email);
      return newSet;
    });
  };

  const getSharedBalance = (credit) => {
    const partner = credits.find(c => c.userEmail === credit.userEmail && c.partnerEmail)?.partnerEmail ||
      credits.find(c => c.partnerEmail === credit.userEmail)?.userEmail;
    return credits.filter(c => c.userEmail === credit.userEmail || c.userEmail === partner)
      .reduce((sum, c) => sum + Number(c.purchased || 0) - Number(c.booked || 0), 0);
  };

  // Группируем записи о покупках по ученику: показываем последнюю покупку,
  // остальные доступны через "Историю покупок"
  const getGroupedCredits = () => {
    const groups = {};
    credits.forEach(credit => {
      const email = credit.userEmail;
      if (!groups[email]) groups[email] = [];
      groups[email].push(credit);
    });

    const getDate = (c) => new Date(c.paymentDate || c.createdAt || 0).getTime();

    return Object.values(groups)
      .map(group => {
        const sorted = [...group].sort((a, b) => getDate(b) - getDate(a));
        return { email: sorted[0].userEmail, latest: sorted[0], history: sorted.slice(1) };
      })
      .sort((a, b) => getDate(b.latest) - getDate(a.latest));
  };

  const renderCreditRow = (credit, { isHistory = false, historyCount = 0, isExpanded = false } = {}) => {
    const remaining = getSharedBalance(credit);
    const isEditing = editingCreditId === credit.id;

    return (
      <tr key={credit.id} style={{ backgroundColor: isHistory ? '#f8f9fa' : (remaining < 0 ? '#ffe6e6' : '#ffffff'), textAlign: 'center' }}>
        <td style={{ padding: '10px', border: '1px solid #ccc' }}>
          {isHistory ? (
            <span style={{ fontSize: '12px', color: '#999' }}>↳ {credit.userName || credit.userEmail}</span>
          ) : (
            <>
              <strong>{credit.userName || credit.userEmail}</strong><br/>
              {credit.kind === 'balanceAdjustment' && <small title={credit.reason}>Корректировка: {credit.reason}<br/></small>}
              <span style={{fontSize: '11px', color: '#666'}}>{isOfflineEmail(credit.userEmail) ? 'без email' : credit.userEmail}</span>
            </>
          )}
        </td>

        <td style={{ padding: '10px', border: '1px solid #ccc' }}>
            {isEditing ? (
                <input type="date" value={editCreditData.paymentDate} onChange={(e) => setEditCreditData({...editCreditData, paymentDate: e.target.value})} />
            ) : (
                credit.paymentDate ? format(new Date(credit.paymentDate), 'dd.MM.yyyy') : (credit.createdAt ? format(new Date(credit.createdAt), 'dd.MM.yyyy') : '—')
            )}
        </td>

        <td style={{ padding: '10px', border: '1px solid #ccc' }}>
           {isEditing ? (
                <input type="number" style={{width: '60px'}} value={editCreditData.purchased} onChange={(e) => setEditCreditData({...editCreditData, purchased: e.target.value})} />
            ) : credit.purchased}
        </td>

        <td style={{ padding: '10px', border: '1px solid #ccc' }}>
           {credit.booked || 0}
        </td>

        <td style={{ padding: '10px', border: '1px solid #ccc', fontWeight: 'bold', color: remaining < 0 ? '#e74c3c' : '#27ae60' }}>
          {remaining}
          {/* Старые пары с общим балансом продолжают работать — подсказываем, откуда число */}
          {credit.partnerEmail && <span title={`Общий баланс с ${credit.partnerEmail}`} style={{ marginLeft: '6px', cursor: 'help' }}>👥</span>}
        </td>

        <td style={{ padding: '10px', border: '1px solid #ccc' }}>
          {isEditing ? (
               <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                  <button disabled={creditSaving} onClick={saveCreditEdit} style={{backgroundColor: '#2ecc71', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>💾</button>
                  <button onClick={cancelCreditEdit} style={{backgroundColor: '#e74c3c', color:'white', border:'none', borderRadius:'4px', cursor:'pointer'}}>✖</button>
               </div>
          ) : (
              <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {!isHistory && historyCount > 0 && (
                    <button onClick={() => toggleCreditHistory(credit.userEmail)} style={{ padding: '5px 8px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }} title="История покупок">
                      {isExpanded ? '▲' : '📜'} {historyCount}
                    </button>
                  )}
                  <button disabled={creditSaving || credit.kind === 'balanceAdjustment'} onClick={() => startEditingCredit(credit)} style={{ padding: '5px 8px', backgroundColor: '#f39c12', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                  <button disabled={creditSaving || credit.kind === 'balanceAdjustment' || credit.everUsed || credit.booked > 0 || !!credit.partnerEmail} title="Удалять можно только неиспользованные покупки" onClick={() => handleDeleteCredit(credit.id)} style={{ padding: '5px 8px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>🗑️</button>
              </div>
          )}
        </td>
      </tr>
    );
  };

  const groupedCredits = getGroupedCredits();

  // Показываем всю полосу дня: часть часов закрыта для учеников (серые), но
  // админу их видно — их можно как заблокировать, так и открыть.
  const nowDate = new Date();
  const minTime = moment(nowDate).hour(7).minute(0).second(0).millisecond(0).toDate();
  const maxTime = moment(nowDate).hour(23).minute(0).second(0).millisecond(0).toDate();


  // Что именно выделено мышью: есть ли там закрытое для учеников время и
  // не попали ли в выделение уже открытые часы (их можно закрыть обратно).
  const pendingInfo = (() => {
    if (!pendingBlock) return null;
    const { start, end } = pendingBlock;
    let hasClosed = false;
    for (let ms = start.getTime(); ms < end.getTime(); ms += 30 * 60 * 1000) {
      if (isSlotUnavailable(new Date(ms), openHours)) { hasClosed = true; break; }
    }
    const overrides = openHours.filter((o) => {
      const iv = openHoursInterval(o);
      return iv.start < end && iv.end > start;
    });
    const sameDay = moment(start).format('YYYY-MM-DD') === moment(new Date(end.getTime() - 1)).format('YYYY-MM-DD');
    const shortEnough = end.getTime() - start.getTime() <= 12 * 60 * 60 * 1000;
    return { hasClosed, overrides, canOpen: hasClosed && sameDay && shortEnough };
  })();

  // Список для поля «Ученик» (с балансом — видно прямо в подсказке).
  const pickerStudents = studentStats.map(s => ({
    email: s.email,
    name: s.name,
    balance: s.creditBalance,
    hasCredits: s.creditPurchased > 0,
    lastLessonAt: s.lessons[0]?.start || '',
  }));

  // Что пересекается с новым уроком (и его недельными повторами): уроки и блокировки.
  const findConflicts = (start, end, repeatCount) => {
    const found = [];
    for (let i = 0; i <= repeatCount; i++) {
      const s = moment(start).add(i, 'weeks').toDate();
      const e = new Date(s.getTime() + end.getTime() - start.getTime());
      const prefix = i > 0 ? moment(s).format('D MMM') + ' ' : '';
      lessons.forEach(l => {
        if (new Date(l.start) < e && new Date(l.end) > s) found.push(`${prefix}${l.userName || l.userEmail} ${moment(l.start).format('HH:mm')}`);
      });
      blockedSlots.forEach(b => {
        if (new Date(b.start) < e && new Date(b.end) > s) found.push(`${prefix}🔒 ${b.title || 'Занято'}`);
      });
    }
    return found;
  };

  // Подсказка в форме «Оплата»: как изменится баланс и сколько долгов закроется.
  const payInfo = (() => {
    if (!payStudent) return null;
    const known = pickerStudents.find(s => s.email === payStudent.email);
    const balance = known?.hasCredits ? known.balance : 0;
    const unpaid = payStudent.email ? lessons.filter(l => l.userEmail === payStudent.email && !l.paid).length : 0;
    const qty = Number(newCredit.purchased) || 0;
    const willPay = Math.min(qty, unpaid);
    return { balance, unpaid, willPay, after: balance + qty - willPay, qty };
  })();

  const selectedLesson = selectedEvent?.type === 'lesson' ? lessons.find(l => l.id === selectedEvent.id) : null;
  const closeSlotModal = () => setPendingBlock(null);

  const calendarEvents = [
    ...lessons.map(lesson => ({
      title: lesson.userName || lesson.userEmail,
      start: new Date(lesson.start),
      end: new Date(lesson.end),
      id: lesson.id,
      type: 'lesson',
    })),
    ...blockedSlots.map(slot => ({
      title: slot.title || 'Занято',
      start: new Date(slot.start),
      end: new Date(slot.end),
      id: slot.id,
      type: 'blocked',
    })),
  ];

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  return (
    <div className="adm-root" style={{ padding: '20px', fontFamily: 'Roboto, sans-serif' }}>
      <h1 style={{ color: '#2c3e50', fontSize: '32px', fontWeight: 'bold' }}>Админ-панель</h1>
      {unpaidLessons.some(l => l.paymentReportedAt) && (
        <div style={{ background: '#faf6fc', color: '#594367', border: '1px solid #e6dced', padding: '14px 18px', borderRadius: '12px', marginBottom: '18px' }}>
          <strong>🌷 Ученики сообщили об оплате — нужно проверить</strong>
          <div>{[...new Set(unpaidLessons.filter(l => l.paymentReportedAt).map(l => l.userName || l.userEmail))].join(', ')}</div>
          <small>После проверки добавьте оплату в разделе «Оплата»: баланс и статусы уроков обновятся.</small>
        </div>
      )}

      {toast && <div key={toast.id} className={'adm-toast' + (toast.kind === 'error' ? ' error' : '')} role="status">{toast.text}</div>}

      {/* Клик по ячейке календаря: записать ученика / заблокировать / открыть время */}
      {pendingBlock && (
        <div className="adm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closeSlotModal(); }}>
          <div className="adm-modal" role="dialog" aria-modal="true">
            <div className="adm-modal-head">
              <div style={{ flex: 1 }}>
                <h3 className="adm-modal-title">{capitalize(format(pendingBlock.start, 'EEEE, d MMMM', { locale: ru }))}</h3>
                <p className="adm-modal-sub">
                  {pendingBlock.allDay ? 'Весь день' : `${format(pendingBlock.start, 'HH:mm')} – ${format(pendingBlock.end, 'HH:mm')}`}
                </p>
              </div>
              <button type="button" className="adm-icon-btn" onClick={closeSlotModal} title="Закрыть (Esc)">✕</button>
            </div>

            <div className="adm-modal-body">
              <div className="adm-segment" role="tablist">
                <button type="button" className={pendingBlock.mode === 'lesson' ? 'on' : ''} onClick={() => setPendingBlock({ ...pendingBlock, mode: 'lesson' })}>📚 Урок</button>
                <button type="button" className={pendingBlock.mode === 'block' ? 'on' : ''} onClick={() => setPendingBlock({ ...pendingBlock, mode: 'block' })}>🔒 Блок</button>
                {(pendingInfo.hasClosed || pendingInfo.overrides.length > 0) && (
                  <button type="button" className={pendingBlock.mode === 'open' ? 'on' : ''} onClick={() => setPendingBlock({ ...pendingBlock, mode: 'open' })}>🔓 Открыть</button>
                )}
              </div>

              {pendingBlock.mode === 'lesson' && (
                <LessonForm
                  key={pendingBlock.start.getTime()}
                  students={pickerStudents}
                  initialStart={pendingBlock.allDay ? moment(pendingBlock.start).hour(10).toDate() : pendingBlock.start}
                  findConflicts={findConflicts}
                  onSubmit={async (data) => {
                    const ok = await bookLessons(data);
                    if (ok) closeSlotModal();
                    return ok;
                  }}
                  onCancel={closeSlotModal}
                />
              )}

              {pendingBlock.mode === 'block' && (
                <>
                  <div className="adm-field">
                    <label className="adm-label">Причина <span className="adm-optional">необязательно</span></label>
                    <input
                      className="adm-input"
                      type="text"
                      placeholder="Занято"
                      autoFocus
                      value={pendingBlock.label}
                      onChange={(e) => setPendingBlock({ ...pendingBlock, label: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmPendingBlock(); }}
                    />
                    <div className="adm-hint">Ученики не смогут записаться на это время. В Google Calendar не попадает.</div>
                  </div>
                  <div className="adm-actions">
                    <button type="button" className="adm-btn adm-btn-ghost" onClick={closeSlotModal}>Отмена</button>
                    <button type="button" className="adm-btn adm-btn-purple" onClick={handleConfirmPendingBlock}>🔒 Заблокировать</button>
                  </div>
                </>
              )}

              {pendingBlock.mode === 'open' && (
                <>
                  {pendingInfo.canOpen && (
                    <>
                      <button type="button" className="adm-btn adm-btn-green adm-btn-block" onClick={handleOpenPendingSlot}>🔓 Открыть для записи</button>
                      <div className="adm-hint">Это время закрыто расписанием — ученики увидят его свободным.</div>
                    </>
                  )}
                  {pendingInfo.hasClosed && !pendingInfo.canOpen && (
                    <div className="adm-hint">Чтобы открыть время, выдели его в режиме «Неделя» или «День» (не больше 12 часов подряд).</div>
                  )}
                  {!pendingInfo.hasClosed && <div className="adm-hint">Это время и так открыто для записи.</div>}
                  {pendingInfo.overrides.length > 0 && (
                    <button type="button" className="adm-btn adm-btn-danger-ghost adm-btn-block" onClick={() => handleClosePendingSlot(pendingInfo.overrides)}>
                      🔁 Закрыть обратно ({pendingInfo.overrides.map((o) => o.startTime + '–' + o.endTime).join(', ')})
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedEvent && (
        <div className="adm-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedEvent(null); }}>
          <div className="adm-modal" role="dialog" aria-modal="true">
            <div className="adm-modal-head">
              {selectedEvent.type === 'lesson' && <div className="adm-avatar">{(selectedEvent.title || '?').slice(0, 1).toUpperCase()}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 className="adm-modal-title">{selectedEvent.type === 'blocked' ? `🔒 ${selectedEvent.title}` : selectedEvent.title}</h3>
                <p className="adm-modal-sub">
                  {capitalize(format(selectedEvent.start, 'EEEE, d MMMM · HH:mm', { locale: ru }))} – {format(selectedEvent.end, 'HH:mm')}
                </p>
                {selectedLesson && !isOfflineEmail(selectedLesson.userEmail) && (
                  <p className="adm-modal-sub" style={{ fontSize: '12px' }}>{selectedLesson.userEmail}</p>
                )}
              </div>
              <button type="button" className="adm-icon-btn" onClick={() => setSelectedEvent(null)} title="Закрыть (Esc)">✕</button>
            </div>
            <div className="adm-modal-body">
              {selectedLesson && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span className={'adm-balance ' + (selectedLesson.paid ? 'pos' : 'zero')} style={{ fontSize: '13px', padding: '4px 12px' }}>
                    {selectedLesson.paid ? '✓ Оплачен' : 'Не оплачен'}
                  </span>
                  <button
                    type="button"
                    className={'adm-btn ' + (selectedLesson.paid ? 'adm-btn-ghost' : 'adm-btn-green')}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => handleTogglePaid(selectedLesson.id, selectedLesson.paid)}
                  >
                    {selectedLesson.paid ? 'Снять оплату' : '💳 Отметить оплаченным'}
                  </button>
                </div>
              )}
              <div className="adm-actions">
                {selectedEvent.type === 'blocked' && (
                  <button type="button" className="adm-btn adm-btn-danger-ghost" onClick={() => handleDeleteBlockedSlot(selectedEvent.id)}>🗑️ Удалить блок</button>
                )}
                {selectedEvent.type === 'lesson' && (
                  <button type="button" className="adm-btn adm-btn-danger-ghost" onClick={() => { handleDeleteLesson(selectedEvent.id); setSelectedEvent(null); }}>🗑️ Удалить урок</button>
                )}
                <button type="button" className="adm-btn adm-btn-ghost" onClick={() => setSelectedEvent(null)}>Закрыть</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. КАЛЕНДАРЬ */}
      <h2>Календарь (общий обзор)</h2>
      <div style={{ marginBottom: '10px', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#3498db', borderRadius: '4px' }}></div>
          <span>Уроки</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#9b59b6', borderRadius: '4px' }}></div>
          <span>Google</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#e3e8f0', borderRadius: '4px', border: '1px solid #cbd3e1' }}></div>
          <span>Закрыто для записи</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: '#d9f7e6', borderRadius: '4px', border: '1px solid #a9e5c3' }}></div>
          <span>Открыто дополнительно</span>
        </div>
        <button onClick={handleClearAllBlockedSlots} style={{ padding: '8px 15px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>
          🗑️ Очистить слоты ({blockedSlots.length})
        </button>
        <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
          💡 Нажми на ячейку — запишешь ученика. Протяни мышью — заблокируешь время или откроешь серое (закрытое) для записи
        </span>
      </div>
      <Calendar
        localizer={localizer}
        culture="ru"
        formats={{
  eventTimeRangeFormat: () => "",
}}
        min={minTime}
        max={maxTime}
        defaultView="week"
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        selectable
        longPressThreshold={150}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        style={{ height: 600, marginBottom: '30px', border: '1px solid #ddd', borderRadius: '8px' }}
        slotPropGetter={(date) => {
          if (isExtraOpen(date, openHours)) return { style: { backgroundColor: '#d9f7e6' } };
          if (isSlotUnavailable(date, openHours)) return { style: { backgroundColor: '#e3e8f0' } };
          return {};
        }}
        dayPropGetter={(date) => (isDayUnavailable(date, openHours) ? { style: { backgroundColor: '#eff2f7' } } : {})}
        eventPropGetter={(event) => {
            if (event.type === 'blocked') return { style: { backgroundColor: '#9b59b6', color: 'white', borderRadius: '4px', cursor: 'pointer' } };
            return { style: { backgroundColor: '#3498db', color: 'white', borderRadius: '4px', cursor: 'pointer' } };
        }}
      />

      {/* Инструменты расписания — свёрнуты, чтобы не отвлекали от календаря */}
      <div className="adm-tools">
        <details className="adm-fold">
          <summary>📚 Урок вручную <span className="adm-fold-sub">— быстрее нажать на ячейку в календаре</span></summary>
          <div className="adm-fold-body">
            <div style={{ maxWidth: '520px' }}>
              <LessonForm students={pickerStudents} findConflicts={findConflicts} onSubmit={bookLessons} submitLabel="Добавить урок" autoFocus={false} />
            </div>
          </div>
        </details>

        <details className="adm-fold adm-fold-purple">
          <summary>🔒 Заблокировать время для записи</summary>
          <div className="adm-fold-body">
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Дата</label>
            <input type="date" value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>С</label>
            <input type="time" value={newBlock.startTime} onChange={(e) => setNewBlock({ ...newBlock, startTime: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>По</label>
            <input type="time" value={newBlock.endTime} onChange={(e) => setNewBlock({ ...newBlock, endTime: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Причина (необязательно)</label>
            <input type="text" placeholder="Занято" value={newBlock.label} onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', minWidth: '160px' }} />
          </div>
          <button onClick={() => setNewBlock({ ...newBlock, startTime: '07:00', endTime: '23:00' })} style={{ padding: '10px 15px', backgroundColor: '#8e44ad', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>
            Весь день
          </button>
          <button onClick={handleAddBlockedSlot} style={{ padding: '10px 20px', backgroundColor: '#6c3483', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>
            🔒 Заблокировать
          </button>
        </div>
        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#7f8c8d' }}>
          ℹ️ Заблокированное время не попадает в Google Calendar — только закрывает запись для учеников
        </p>
          </div>
        </details>

        <details className="adm-fold adm-fold-green">
          <summary>🔓 Открыть часы сверх расписания <span className="adm-fold-sub">— вечер или выходной</span></summary>
          <div className="adm-fold-body">
        <p style={{ margin: '0 0 15px 0', fontSize: '13px', color: '#5d6d7e' }}>
          Обычно запись открыта 08:00–21:00 по Риму каждый день, включая субботу и воскресенье. Здесь можно открыть ученикам
          отдельный вечер — например 18:00–20:00 в конкретный день. Время указывается в твоём часовом поясе.
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Дата</label>
            <input type="date" value={newOpenHours.date} onChange={(e) => setNewOpenHours({ ...newOpenHours, date: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>С</label>
            <input type="time" step="1800" value={newOpenHours.startTime} onChange={(e) => setNewOpenHours({ ...newOpenHours, startTime: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>По</label>
            <input type="time" step="1800" value={newOpenHours.endTime} onChange={(e) => setNewOpenHours({ ...newOpenHours, endTime: e.target.value })} style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
          </div>
          <button onClick={handleAddOpenHours} style={{ padding: '10px 20px', backgroundColor: '#1e8449', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>
            🔓 Открыть
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Быстро:</span>
          {[['17:00', '19:00'], ['17:00', '20:00'], ['18:00', '20:00'], ['18:00', '22:00']].map(([from, to]) => (
            <button
              key={from + to}
              onClick={() => setNewOpenHours({ ...newOpenHours, startTime: from, endTime: to })}
              style={{ padding: '6px 12px', backgroundColor: '#eafaf1', color: '#1e8449', border: '1px solid #a9e5c3', borderRadius: '20px', cursor: 'pointer', fontSize: '13px' }}
            >
              {from}–{to}
            </button>
          ))}
        </div>

        {/* Список "Открытые дни" скрыт по просьбе — открытые часы теперь видны прямо в календаре выше.
        <div style={{ marginTop: '20px' }}>
          <strong style={{ fontSize: '14px', color: '#2c3e50' }}>Открытые дни ({upcomingOpenHours.length})</strong>
          {upcomingOpenHours.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#7f8c8d', margin: '8px 0 0 0' }}>Пока ничего не открыто сверх расписания.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {upcomingOpenHours.map((o) => (
                <li key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 12px', backgroundColor: 'white', border: '1px solid #d5f0e0', borderRadius: '6px', maxWidth: '420px' }}>
                  <span style={{ fontSize: '14px', color: '#2c3e50' }}>
                    {format(moment(o.date, 'YYYY-MM-DD', true).toDate(), 'EEEE, d MMMM', { locale: ru })} · <strong>{o.startTime}–{o.endTime}</strong>
                  </span>
                  <button onClick={() => handleDeleteOpenHours(o.id)} style={{ padding: '4px 10px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>
                    Закрыть
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        */}
        <p style={{ margin: '15px 0 0 0', fontSize: '12px', color: '#7f8c8d' }}>
          ℹ️ Открытые часы сильнее любых ограничений расписания — так можно открыть вечер даже в субботу.
          Но конкретные заблокированные слоты (🔒) всё равно останутся закрытыми.
        </p>
          </div>
        </details>
      </div>

      {/* 2. ОПЛАТА */}
      <div className="adm-card">
        <h3 className="adm-card-title">💳 Оплата</h3>
        <div className="adm-pay-grid">
          <div className="adm-field">
            <label className="adm-label">Ученик</label>
            <StudentPicker students={pickerStudents} value={payStudent} onChange={setPayStudent} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Оплачено уроков</label>
            <div className="adm-pay-qty">
              {[1, 4, 8, 10].map(n => (
                <button key={n} type="button" className={'adm-chip' + (Number(newCredit.purchased) === n ? ' on' : '')} onClick={() => setNewCredit({ ...newCredit, purchased: n })}>{n}</button>
              ))}
              <input className="adm-input" type="number" min="1" placeholder="др." value={newCredit.purchased} onChange={(e) => setNewCredit({ ...newCredit, purchased: e.target.value })} />
            </div>
          </div>
          <div className="adm-field">
            <label className="adm-label">Дата оплаты</label>
            <input className="adm-input" type="date" value={newCredit.paymentDate} onChange={(e) => setNewCredit({ ...newCredit, paymentDate: e.target.value })} />
          </div>
        </div>
        {payInfo && (
          <div className="adm-preview">
            <span>
              Баланс: <strong>{payInfo.balance}</strong>
              {payInfo.qty > 0 && <> → <strong style={{ color: payInfo.after >= 0 ? '#1e8449' : '#c0392b' }}>{payInfo.after}</strong></>}
            </span>
            {payInfo.unpaid > 0 && (
              <span>
                Неоплаченных уроков: <strong>{payInfo.unpaid}</strong>
                {payInfo.willPay > 0 && ` — закроется ${payInfo.willPay}`}
              </span>
            )}
          </div>
        )}
        <div className="adm-actions" style={{ marginTop: '14px' }}>
          <button type="button" className="adm-btn adm-btn-green" onClick={handleAddCredit} disabled={paymentSaving || !payStudent || !(Number(newCredit.purchased) > 0)}>
            💳 Записать оплату
          </button>
        </div>
      </div>

      {/* 3. ТАБЛИЦА КРЕДИТОВ */}
      <h2>История оплат <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#7f8c8d' }}>(учеников: {groupedCredits.length})</span></h2>
      <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#7f8c8d' }}>
        Показана последняя оплата каждого ученика. Чтобы увидеть предыдущие, нажмите 📜 в строке ученика.
      </p>
      <div style={{ overflowX: 'auto', marginBottom: '40px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
          <thead>
            <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Имя / Email</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Дата оплаты</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Куплено</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Использовано</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Осталось</th>
              <th style={{ padding: '10px', border: '1px solid #ccc' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {groupedCredits.map(({ email, latest, history }) => {
              const isExpanded = expandedCreditHistory.has(email);
              return (
                <React.Fragment key={email}>
                  {renderCreditRow(latest, { historyCount: history.length, isExpanded })}
                  {isExpanded && history.map(credit => renderCreditRow(credit, { isHistory: true }))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 4. ИСТОРИЯ ПО УЧЕНИКАМ */}
      <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#e8f5e9', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
          📊 История по ученикам <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#7f8c8d' }}>(всего: {studentStats.length})</span>
        </h2>

        {/* Добавление ученика, у которого ещё нет уроков (только что зарегистрировался) */}
        <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#2c3e50', marginBottom: '4px' }}>
            ➕ Добавить ученика и открыть бронирование
          </div>
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '10px' }}>
            Для тех, кто только зарегистрировался и ещё не имеет уроков. Email должен точно совпадать с тем, что в Firebase Auth.
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="email"
              placeholder="Email ученика"
              value={newStudent.email}
              onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
              style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', flex: 1, minWidth: '220px' }}
            />
            <input
              type="text"
              placeholder="Имя (необязательно)"
              value={newStudent.name}
              onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
              style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', flex: 1, minWidth: '160px' }}
            />
            <button
              onClick={handleAddStudentAccess}
              style={{ padding: '10px 20px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              🔓 Добавить и открыть
            </button>
          </div>
        </div>

        {hiddenStudentsList.length > 0 && (
          <div style={{ marginBottom: '15px', padding: '12px 15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeeba' }}>
            <div style={{ fontSize: '13px', color: '#856404', marginBottom: '8px' }}>
              👁️ Скрытые ученики ({hiddenStudentsList.length}):
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {hiddenStudentsList.map(h => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'white', padding: '6px 12px', borderRadius: '20px', border: '1px solid #ddd' }}>
                  <span style={{ fontSize: '13px' }}>{h.name || h.email}</span>
                  <button onClick={() => handleUnhideStudent(h.id, h.name)} style={{ padding: '4px 10px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                    Вернуть
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {studentStats.map(student => (
          <div key={student.email} style={{ marginBottom: '10px', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
            <div onClick={() => toggleStudentExpand(student.email)} style={{ padding: '15px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: expandedStudents.has(student.email) ? '#f8f9fa' : 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <span style={{ fontSize: '12px', color: '#7f8c8d' }}>{expandedStudents.has(student.email) ? '▼' : '▶'}</span>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '16px' }}>{student.name}</div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                    {isOfflineEmail(student.email) ? <span className="adm-tag adm-tag-muted">без email</span> : student.email}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#7f8c8d' }}>Прошло</div><div style={{ fontWeight: 'bold', color: '#2c3e50' }}>{student.pastLessons}</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#7f8c8d' }}>Оплачено</div><div style={{ fontWeight: 'bold', color: '#27ae60' }}>{student.paidLessons}</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#7f8c8d' }}>Не оплачено</div><div style={{ fontWeight: 'bold', color: '#e74c3c' }}>{student.unpaidLessons}</div></div>
                <div style={{ textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#7f8c8d' }}>Впереди</div><div style={{ fontWeight: 'bold', color: '#3498db' }}>{student.futureLessons}</div></div>
                <div style={{ textAlign: 'center', padding: '5px 15px', backgroundColor: student.creditBalance > 0 ? '#d4edda' : student.creditBalance < 0 ? '#f8d7da' : '#fff3cd', borderRadius: '20px' }}>
                  <div style={{ fontSize: '11px', color: '#666' }}>Баланс</div>
                  {editingBalanceEmail === student.email ? (
                    <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <input
                        type="number"
                        value={editBalanceValue}
                        onChange={(e) => setEditBalanceValue(e.target.value)}
                        style={{ width: '50px', padding: '2px 4px', fontSize: '13px' }}
                        autoFocus
                      />
                      <input type="text" value={editBalanceReason} onChange={e => setEditBalanceReason(e.target.value)} placeholder="Причина" maxLength={300} style={{width:'130px'}} />
                      <button disabled={creditSaving} onClick={() => saveBalanceEdit(student)} style={{ padding: '2px 6px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>✔</button>
                      <button onClick={cancelBalanceEdit} style={{ padding: '2px 6px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>✖</button>
                    </div>
                  ) : (
                    <div
                      onClick={(e) => { e.stopPropagation(); startEditingBalance(student); }}
                      style={{ fontWeight: 'bold', color: student.creditBalance > 0 ? '#155724' : student.creditBalance < 0 ? '#721c24' : '#856404', cursor: student.creditPurchased > 0 ? 'pointer' : 'default' }}
                      title="Исправить остаток отдельной записью"
                    >
                      {`${student.creditBalance} ✏️`}
                    </div>
                  )}
                </div>
                {/* Ученик без email не может войти на сайт — бронь ему открывать незачем */}
                {!isOfflineEmail(student.email) && (
                  <div style={{ textAlign: 'center', padding: '5px 10px', borderRadius: '20px', backgroundColor: studentAccessMap[student.email]?.bookingEnabled ? '#d4edda' : '#f8d7da' }}>
                    <div style={{ fontSize: '11px', color: '#666' }}>Бронь</div>
                    <div style={{ fontWeight: 'bold', color: studentAccessMap[student.email]?.bookingEnabled ? '#155724' : '#721c24', fontSize: '13px' }}>{studentAccessMap[student.email]?.bookingEnabled ? '✓' : '✗'}</div>
                  </div>
                )}
                {studentAccessMap[student.email]?.blockedUntil && new Date(studentAccessMap[student.email].blockedUntil) > now && (
                  <div style={{ textAlign: 'center', padding: '5px 10px', borderRadius: '20px', backgroundColor: '#f8d7da' }} title="Временно заблокирован">
                    <div style={{ fontSize: '11px', color: '#666' }}>До</div>
                    <div style={{ fontWeight: 'bold', color: '#721c24', fontSize: '13px' }}>
                      {format(new Date(studentAccessMap[student.email].blockedUntil), 'dd.MM.yyyy', { locale: ru })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {expandedStudents.has(student.email) && (
              <div style={{ padding: '0 20px 20px', borderTop: '1px solid #eee' }}>
                {/* ИЗМЕНЕНО: Кнопка СКРЫТИЯ ученика (не удаляет из базы) */}
                <div style={{marginTop: '10px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center'}}>
                    {studentAccessMap[student.email]?.blockedUntil && new Date(studentAccessMap[student.email].blockedUntil) > now && (
                      <span style={{ fontSize: '12px', color: '#721c24', marginRight: 'auto' }}>
                        🔒 Заблокирован до {format(new Date(studentAccessMap[student.email].blockedUntil), 'dd.MM.yyyy', { locale: ru })}
                        {' '}
                        <button
                            onClick={() => handleClearTemporaryBlock(student.email)}
                            style={{ padding: '4px 8px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' }}
                        >
                            Снять
                        </button>
                      </span>
                    )}
                    {!isOfflineEmail(student.email) && (<>
                    <input
                        type="date"
                        value={blockUntilDrafts[student.email] || ''}
                        onChange={(e) => setBlockUntilDrafts(prev => ({ ...prev, [student.email]: e.target.value }))}
                        style={{ padding: '7px 8px', border: '1px solid #ddd', borderRadius: '5px', fontSize: '12px' }}
                    />
                    <button
                        onClick={() => handleSetTemporaryBlock(student.email, student.name, blockUntilDrafts[student.email])}
                        style={{ padding: '8px 12px', backgroundColor: '#c0392b', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}
                        title="Заблокировать бронирование этому ученику до выбранной даты"
                    >
                        ⏳ Заблокировать до даты
                    </button>
                    <button
                        onClick={() => handleToggleBookingAccess(student.email, student.name, !!studentAccessMap[student.email]?.bookingEnabled)}
                        style={{ padding: '8px 12px', backgroundColor: studentAccessMap[student.email]?.bookingEnabled ? '#e67e22' : '#27ae60', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}
                    >
                        {studentAccessMap[student.email]?.bookingEnabled ? '🔒 Закрыть бронирование' : '🔓 Открыть бронирование'}
                    </button>
                    </>)}
                    <button
                        onClick={() => handleHideStudent(student.email, student.name)}
                        style={{ padding: '8px 12px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}
                    >
                        👁️ Скрыть ученика из списка
                    </button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginTop: '10px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Дата урока</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Статус</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Оплата</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const renderLessonRow = (lesson) => {
                        const lessonDate = new Date(lesson.start);
                        const isPast = lessonDate < now;
                        return (
                          <tr key={lesson.id} style={{ backgroundColor: isPast ? '#fafafa' : '#fff' }}>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{format(lessonDate, 'dd MMMM yyyy, HH:mm', { locale: ru })}</td>
                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                              <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', backgroundColor: isPast ? '#6c757d' : '#007bff', color: 'white' }}>{isPast ? 'Прошёл' : 'Впереди'}</span>
                            </td>
                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                              <button onClick={() => handleTogglePaid(lesson.id, lesson.paid)} style={{ padding: '5px 12px', backgroundColor: lesson.paid ? '#28a745' : '#ffc107', color: lesson.paid ? 'white' : '#212529', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>{lesson.paid ? '✓ Оплачено' : '○ Не оплачено'}</button>
                              {!lesson.paid && lesson.paymentReportedAt && <div style={{ color: '#6b5288', fontSize: '12px', marginTop: '6px' }} title={format(new Date(lesson.paymentReportedAt), 'dd MMMM yyyy, HH:mm', { locale: ru })}>🌷 Ученик сообщил об оплате · проверить</div>}
                            </td>
                            <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                              <button onClick={() => handleDeleteLesson(lesson.id)} style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>🗑️</button>
                            </td>
                          </tr>
                        );
                      };

                      // student.lessons отсортированы по убыванию даты (новые сверху)
                      const futureLessons = student.lessons.filter(l => new Date(l.start) >= now);
                      const pastLessons = student.lessons.filter(l => new Date(l.start) < now);
                      const showAllPast = expandedPastLessons.has(student.email);
                      const PAST_PREVIEW = 5;
                      const visiblePast = showAllPast ? pastLessons : pastLessons.slice(0, PAST_PREVIEW);
                      const hiddenCount = pastLessons.length - visiblePast.length;

                      return (
                        <>
                          {futureLessons.map(renderLessonRow)}
                          {visiblePast.map(renderLessonRow)}
                          {pastLessons.length > PAST_PREVIEW && (
                            <tr>
                              <td colSpan={4} style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', backgroundColor: '#f0f4f8' }}>
                                <button
                                  onClick={() => togglePastLessons(student.email)}
                                  style={{ padding: '6px 16px', backgroundColor: '#607d8b', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}
                                >
                                  {showAllPast ? '▲ Свернуть прошедшие' : `▼ Показать все прошедшие (${hiddenCount} скрыто)`}
                                </button>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminDashboard;
